import { FC, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import styled, { css } from 'styled-components'

import Table, { TableHeader } from 'components/Table'
import { Body } from 'components/Text'
import { useAppDispatch, useAppSelector } from 'state/hooks'
import { selectBurnLiquidityStatus, selectClaimEarningStatus, selectCollectLiquidityStatus, selectCurrentMarketInfo, selectUserLiquidities, selectUserLiquiditiesQueryStatus } from 'state/exchange/selectors'
import { formatNumber } from 'utils/prices'
import { FetchStatus } from 'state/types'
import { burnLiquidity, collectLiquidity, fetchUserLiquidities } from 'state/exchange/actions'
import Pill from 'components/Pill'
import Tooltip from 'components/Tooltip/Tooltip'

type MyLiquiditiesProps = {
	mobile?: boolean
	display?: boolean
}

enum TableColumnAccessor {
	PriceLow = 'priceLow',
	PriceHigh = 'priceHigh',
	AmountX = 'amountX',
	AmountY = 'amountY',
	EarnX = 'earnX',
	EarnY = 'earnY',
	Claim = 'claim',
	Burn = 'burn',
}

const MyLiquidities: FC<MyLiquiditiesProps> = ({ mobile, display }) => {
	const { t } = useTranslation()
	const dispatch = useAppDispatch()
	const collectLiquidityStatus = useAppSelector(selectCollectLiquidityStatus)
	const burnLiquidityStatus = useAppSelector(selectBurnLiquidityStatus)
	const marketInfo = useAppSelector(selectCurrentMarketInfo)
	const userLiquidities = useAppSelector(selectUserLiquidities)
	const userLiquiditiesQueryStatus = useAppSelector(selectUserLiquiditiesQueryStatus)

	useEffect(() => {
		if (!marketInfo) return
		dispatch(fetchUserLiquidities())
	}, [dispatch, marketInfo])

	const data = useMemo(() => {
		return userLiquidities
	}, [userLiquidities])

	return (
		<StyledTable
			data={data}
			isLoading={userLiquiditiesQueryStatus.status === FetchStatus.Loading}
			highlightRowsOnHover
			columnsDeps = {[marketInfo, dispatch, collectLiquidityStatus, burnLiquidityStatus, t]}
			columns={[
				{
					header: () => <TableHeader>{t('futures.market.user.liquidities.table.price-low')}</TableHeader>,
					accessorKey: TableColumnAccessor.PriceLow,
					enableSorting: false,
					cell: (cellProps) => {
						return (
							<PriceValue>
								{formatNumber(cellProps.row.original.priceLow, { suggestDecimals: true })}
							</PriceValue>
						)
					},
					size: 100,
				},
				{
					header: () => <TableHeader>{t('futures.market.user.liquidities.table.price-high')} {!mobile ?? `(${marketInfo?.tokenY.symbol})`}</TableHeader>,
					accessorKey: TableColumnAccessor.PriceHigh,
					enableSorting: true,
					cell: (cellProps) => {
						return (
							<PriceValue>
								{formatNumber(cellProps.row.original.priceHigh, { suggestDecimals: true })}
							</PriceValue>
						)
					},
					size: 100,
				},
				{
					header: () => <TableHeader>{t('futures.market.user.liquidities.table.amount-x', { tokenX: marketInfo?.tokenX.symbol ?? '' })}</TableHeader>,
					accessorKey: TableColumnAccessor.AmountX,
					enableSorting: false,
					cell: (cellProps) => {
						return (
							<PriceValue>
								{formatNumber(Math.abs(cellProps.row.original.amountX), {
									suggestDecimals: true,
									truncateOver: 1e6,
									maxDecimals: 6,
								})}
							</PriceValue>
						)
					},
					size: 100,
				},
				{
					header: () => <TableHeader>{t('futures.market.user.liquidities.table.amount-y', { tokenY: marketInfo?.tokenY.symbol ?? '' })}</TableHeader>,
					accessorKey: TableColumnAccessor.AmountY,
					enableSorting: false,
					cell: (cellProps) => {
						return (
							<PriceValue>
								{formatNumber(Math.abs(cellProps.row.original.amountY), {
									suggestDecimals: true,
									truncateOver: 1e6,
									maxDecimals: 6,
								})}
							</PriceValue>
						)
					},
					size: 100,
				},
				{
					header: () => <TableHeader>{t('futures.market.user.liquidities.table.earn-x', { tokenX: marketInfo?.tokenX.symbol ?? '' })}</TableHeader>,
					accessorKey: TableColumnAccessor.EarnX,
					enableSorting: false,
					cell: (cellProps) => {
						return (
							<PriceValue>
								{formatNumber(Math.abs(cellProps.row.original.earningX), {
									suggestDecimals: true,
									truncateOver: 1e6,
									maxDecimals: 6,
								})}
							</PriceValue>
						)
					},
					size: 100,
				},
				{
					header: () => <TableHeader>{t('futures.market.user.liquidities.table.earn-y', { tokenY: marketInfo?.tokenY.symbol ?? '' })}</TableHeader>,
					accessorKey: TableColumnAccessor.EarnY,
					enableSorting: false,
					cell: (cellProps) => {
						return (
							<PriceValue>
								{formatNumber(Math.abs(cellProps.row.original.earningY), {
									suggestDecimals: true,
									truncateOver: 1e6,
									maxDecimals: 6,
								})}
							</PriceValue>
						)
					},
					size: 100,
				},
				{
					header: () =>
						<StyledTooltip content={'Earnings of the liquidity should be claimed manually'}>
							<TableHeader>{t('futures.market.user.liquidities.table.claim')}</TableHeader>
						</StyledTooltip>,
					accessorKey: TableColumnAccessor.Claim,
					enableSorting: false,
					cell: (cellProps) => {
						const lid = cellProps.row.original.lid
						const earningX = cellProps.row.original.earningX
						const earningY = cellProps.row.original.earningY
						const status = collectLiquidityStatus[`${marketInfo?.marketAddress ?? ''}-${lid}`]

						return (
							<Pill
								disabled={(earningX === 0 && earningY === 0) || status === FetchStatus.Loading || status === FetchStatus.Success}
								onClick={() => dispatch(collectLiquidity({ market: marketInfo?.marketAddress ?? '', lid }))}
							>
								{t('futures.market.user.liquidities.action.claim')}
							</Pill>
						)
					},
					size: 100,
				},
				{
					header: () =>
						<StyledTooltip content={'Burn will remove liquidity and claim earnings'}>
							<TableHeader>{t('futures.market.user.liquidities.table.burn')}</TableHeader>
						</StyledTooltip>,
					accessorKey: TableColumnAccessor.Burn,
					enableSorting: false,
					cell: (cellProps) => {
						const lid = cellProps.row.original.lid
						const status = burnLiquidityStatus[`${marketInfo?.marketAddress ?? ''}-${lid}`]
						return (
							<Pill
								disabled={status === FetchStatus.Loading || status === FetchStatus.Success}
								onClick={() => dispatch(burnLiquidity({ market: marketInfo?.marketAddress ?? '', lid }))}
							>
								{t('futures.market.user.liquidities.action.burn')}
							</Pill>
						)
					},
					size: 100,
				},
			]}
		/>
	)
}

export default MyLiquidities

const StyledTooltip = styled(Tooltip).attrs({ position: 'auto', height: 'auto' })<{
	mobile?: boolean
}>`
	z-index: 2;
	padding: 10px;
	max-width: 300px;
	right: ${(props) => props.mobile && '1px'};
`

const OrderbookContainer = styled.div<{ mobile?: boolean; $display?: boolean }>`
	box-sizing: border-box;
	border-left: ${(props) => props.theme.colors.selectedTheme.border};
	height: 100%;
	width: ${(props) => (props.mobile ? '100%' : '300px')};
	${(props) =>
		props.mobile &&
		css`
			height: 100%;
			margin-bottom: 0;
			border-radius: 0;
			border: none;
			border-bottom: ${(props) => props.theme.colors.selectedTheme.border};
		`};

	${(props) =>
		!props.$display &&
		css`
			display: none;
		`}
`

const TableAlignment = css`
	justify-content: space-between;
	& > div:first-child {
		flex: 70 70 0 !important;
	}
	& > div:nth-child(2) {
		flex: 100 100 0 !important;
		justify-content: center;
	}
	& > div:last-child {
		flex: 60 60 0 !important;
		justify-content: flex-end;
		padding-right: 20px;
	}
`

const StyledTable = styled(Table)`
	border: none;
	overflow-y: scroll;

	.table-row,
	.table-body-row {
		${TableAlignment}
		padding: 0;
	}
	.table-body-cell {
		height: 30px;
	}
` as typeof Table

const PriceValue = styled(Body).attrs({ mono: true })`
	color: ${(props) => props.theme.colors.selectedTheme.button.text.primary};
	padding-left: 5px;
`

const TimeValue = styled(Body)`
	color: ${(props) => props.theme.colors.selectedTheme.button.text.primary};
`